import { createRoot } from 'react-dom/client';
import { Chart, registerables } from 'chart.js';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiResponseError } from './api/core/_models';
import { getErrorMessage } from './api/core/axiosErrorMapper';
import { SystemNotification } from './app/components/feedback/SystemNotification';
// Apps
import { MetronicI18nProvider } from './_metronic/i18n/Metronici18n';
import './_metronic/assets/sass/style.react.scss';
import './_metronic/assets/fonticon/fonticon.css';
import './_metronic/assets/keenicons/duotone/style.css';
import './_metronic/assets/keenicons/outline/style.css';
import './_metronic/assets/keenicons/solid/style.css';
/**
 * TIP: Replace this style import with rtl styles to enable rtl mode
 *
 * import './_metronic/assets/css/style.rtl.css'
 **/
import './_metronic/assets/sass/style.scss';
import { AppRoutes } from './app/routing/AppRoutes';
import { setupAxios } from './api/axios';
import { ToastContainer } from 'react-toastify';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { AuthProvider } from './app/modules/auth/core/Auth';
/**
 * Creates `axios-mock-adapter` instance for provided `axios` instance, add
 * basic Metronic mocks and returns it.
 *
 * @see https://github.com/ctimmerm/axios-mock-adapter
 */
/**
 * Inject Metronic interceptors for axios.
 *
 * @see https://github.com/axios/axios#interceptors
 */
setupAxios();
Chart.register(...registerables);

const isForbidden = (error: unknown) => error instanceof ApiResponseError && error.status === 403;

const queryClient = new QueryClient({
  // A 403 on a secondary query (a select's options, a widget) must not take the whole page
  // down: the component renders empty and the user gets one warning instead.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!isForbidden(error) || query.meta?.suppressGlobalError) return;
      SystemNotification.warning(getErrorMessage(error), { toastId: 'query-forbidden' });
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
      retry: (failureCount, error) => !isForbidden(error) && failureCount < 1,
      staleTime: 0,
      // v5: query errors surface through the nearest ErrorBoundary instead of a
      // global toast. Queries that render their own fallback (optional endpoints,
      // graceful degradation) keep opting out via meta.suppressGlobalError.
      throwOnError: (error, query) => !query.meta?.suppressGlobalError && !isForbidden(error),
    },
    mutations: {
      retry: 0, // Não tentar novamente em caso de falha
    },
  },
});
const container = document.getElementById('root');
if (container) {
  createRoot(container).render(
    <QueryClientProvider client={queryClient}>
      <NuqsAdapter>
        <MetronicI18nProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </MetronicI18nProvider>
        <ToastContainer />
      </NuqsAdapter>
    </QueryClientProvider>,
  );
}
